import React, { useState } from "react";
import { normalizeCoinImageUri } from "@/lib/imageUtils";
import { tokenMetaCache } from "@/lib/tokenMetaCache";

const GRADIENTS = [
  "from-emerald-500 via-teal-600 to-cyan-700",
  "from-amber-500 via-orange-600 to-red-700",
  "from-purple-500 via-indigo-600 to-blue-700",
  "from-pink-500 via-rose-600 to-red-700",
  "from-sky-400 via-blue-600 to-indigo-800",
  "from-fuchsia-500 via-purple-600 to-violet-800",
  "from-yellow-400 via-amber-600 to-orange-700",
  "from-lime-400 via-emerald-600 to-teal-800",
];

export function getGradientForMint(mint?: string, symbol?: string) {
  const str = mint || symbol || "SOLCLAW";
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  const idx = Math.abs(hash) % GRADIENTS.length;
  return GRADIENTS[idx];
}

export interface TokenAvatarProps {
  mint?: string;
  symbol?: string | null;
  name?: string | null;
  imageUri?: string | null;
  size?: string; // e.g. "size-10", "size-12", "size-8", "w-10 h-10"
  animated?: boolean;
  badge?: React.ReactNode; // e.g. "🔥", "🚀", "⚡", "🐋"
  className?: string;
  delayMs?: number;
}

export const TokenAvatar: React.FC<TokenAvatarProps> = ({
  mint,
  symbol,
  name,
  imageUri,
  size = "size-10",
  animated = true,
  badge,
  className = "",
  delayMs = 0,
}) => {
  const [imgError, setImgError] = useState(false);
  React.useEffect(() => setImgError(false), [imageUri, mint]);

  const cached = mint ? tokenMetaCache.get(mint) : undefined;
  const normalizedImageUri = normalizeCoinImageUri(imageUri || cached?.imageUri, mint);
  const resolvedSymbol = symbol || cached?.symbol || (mint ? mint.slice(0, 4).toUpperCase() : null);
  const resolvedName = name || cached?.name || (mint ? `Token ${mint.slice(0, 4)}` : null);

  const gradientClass = getGradientForMint(mint, resolvedSymbol || resolvedName || undefined);

  return (
    <div className={`relative inline-flex shrink-0 items-center justify-center ${size} ${className} group`}>
      {/* Outer Glowing Pulsing Halo */}
      {animated && (
        <div 
          style={{ animationDelay: `${delayMs}ms` }}
          className="absolute -inset-1.5 rounded-full bg-gradient-to-tr from-emerald-500/60 via-sky-400/50 to-amber-400/60 blur-[4px] opacity-75 group-hover:opacity-100 transition-opacity animate-pulse-ring pointer-events-none" 
        />
      )}

      {/* Floating & Spinning Glow Ring on Hover */}
      <div 
        style={{ animationDelay: `${(delayMs + 600) % 3000}ms` }}
        className={`relative size-full rounded-full p-[1.5px] bg-gradient-to-br from-white/40 via-white/10 to-transparent shadow-xl ${animated ? "animate-float-token" : ""}`}
      >
        <div className="size-full rounded-full overflow-hidden bg-black/80 flex items-center justify-center">
          {normalizedImageUri && !imgError ? (
            <img
              src={normalizedImageUri}
              alt={resolvedSymbol || resolvedName || "token"}
              onError={() => setImgError(true)}
              className="size-full object-cover transition-transform duration-500 group-hover:scale-110"
            />
          ) : (
            <div
              className={`flex size-full items-center justify-center bg-gradient-to-br ${gradientClass} text-white font-black shadow-inner transition-transform duration-500 group-hover:scale-110`}
            >
              <span className="text-[11px] sm:text-xs font-mono font-black drop-shadow-md select-none text-white/95">
                ?
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Floating Micro-Badge */}
      {badge && (
        <div className="absolute -bottom-1 -right-1 z-10 flex size-4 sm:size-5 items-center justify-center rounded-full bg-black/90 border border-white/30 text-[9px] sm:text-[11px] shadow-lg animate-bounce pointer-events-none">
          {badge}
        </div>
      )}
    </div>
  );
};
